import { db } from './database'
import { loadSummary } from './budget'
import { daysSinceLastSync } from './sync'
import { ensureNotification, setRead } from './notifications'
import { cycleRange } from '../lib/cycles'
import { derivedId } from '../lib/uuid'
import { dayTarget } from '../lib/notification-target'
import { formatDay, today } from '../lib/dates'
import { formatAriary } from '../lib/money'

/**
 * Le RATTRAPAGE : ce que l'app a à dire, recalculé à chaque ouverture.
 *
 * ⚠️ C'est ici, et nulle part ailleurs, que naissent les notifications. Le Web
 * ne sait pas programmer un réveil pour une date future (décision 2026-09-05) :
 * il n'y a donc pas de « moment où la notification part ». Il y a un moment où
 * l'utilisateur ouvre l'app, et là on regarde ce qui s'est accumulé.
 *
 * Trois propriétés tiennent tout l'édifice, et les perdre ne produit AUCUNE
 * erreur — seulement une boîte inutilisable :
 *
 * 1. **Identifiants calculés.** Chaque notification a un identifiant dérivé de
 *    ce qui la justifie (`vision.md`, « L'identifiant CALCULÉ »). Rouvrir l'app
 *    dix fois ne fabrique pas dix lignes, et deux appareils du même compte
 *    écrivent la MÊME ligne au lieu de deux.
 * 2. **Le compte entre dans la graine.** Sans lui, deux membres d'un même
 *    portefeuille dériveraient le même identifiant pour la même enveloppe
 *    dépassée — et se voleraient mutuellement leur notification à la
 *    synchronisation.
 * 3. **Une notification écartée ne revient jamais.** `ensureNotification`
 *    refuse d'écrire par-dessus un tombstone. Sans ça, supprimer une
 *    notification la ferait réapparaître à la réouverture suivante : c'est le
 *    genre de détail qui fait désinstaller une app.
 *
 * Ce qui est notifié ici a été arrêté avec Faly le 2026-09-05. Trois candidats
 * ont été explicitement ÉCARTÉS, et il ne faut pas les rajouter sans le
 * redemander : le nouvel appareil connecté, la relance des journées en retard,
 * et l'enveloppe bientôt épuisée.
 */

/** Le préfixe de graine, pour que deux règles ne se croisent jamais. */
const SEED = 'notification'

export interface CatchUpInput {
  walletId: string
  accountId: string
  /** La date du jour, injectable — le voyage dans le temps du Profil s'en sert. */
  date?: string
}

export async function catchUpNotifications(
  { walletId, accountId, date = today() }: CatchUpInput,
): Promise<void> {
  await Promise.all([
    daysToConfirm({ walletId, accountId, date }),
    overspentEnvelopes({ walletId, accountId, date }),
    reachedGoals({ walletId, accountId, date }),
    staleSync({ accountId }),
    pendingConflicts({ accountId }),
  ])
}

/* ─────────────────────────────────────────────────────────────────────────
   1. Les journées prévues à confirmer
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Une notification par JOURNÉE, jamais par journée-enveloppe.
 *
 * Une enveloppe « jour par jour » sur un mois produit trente allocations : une
 * notification chacune noierait la boîte au point de la rendre inutile. On
 * regroupe donc par date — c'est aussi ce que l'utilisateur a en tête, « il y a
 * un truc à confirmer pour hier ».
 *
 * ⚠️ Elle se marque lue TOUTE SEULE dès qu'il ne reste plus rien à confirmer ce
 * jour-là (demande de Faly, 2026-09-05). Confirmer puis retrouver la
 * notification en gras donnerait l'impression que le geste n'a pas pris.
 */
async function daysToConfirm({ walletId, accountId, date }: Required<CatchUpInput>) {
  const wallet = await db.wallets.get(walletId)
  const range = cycleRange(date, wallet?.cycle ?? 'monthly')

  const allocations = (await db.budget_allocations.where({ wallet_id: walletId }).toArray())
    .filter((a) => !a.deleted_at)
    // Le cycle en cours seulement : rouvrir l'app après trois mois d'absence ne
    // doit pas déverser quatre-vingt-dix notifications d'un coup.
    .filter((a) => a.planned_date >= range.start && a.planned_date <= date)

  // Par jour : ce qui reste à confirmer, et ce qui a déjà été traité.
  const byDay = new Map<string, { pending: number; total: number; amount: number }>()
  for (const allocation of allocations) {
    const day = byDay.get(allocation.planned_date) ?? { pending: 0, total: 0, amount: 0 }
    day.total += 1
    // `?? null` : une ligne écrite avant l'existence du champ vaut `undefined`.
    if ((allocation.confirmed_at ?? null) === null) {
      day.pending += 1
      day.amount += allocation.planned_amount
    }
    byDay.set(allocation.planned_date, day)
  }

  for (const [day, counts] of byDay) {
    const id = await seedId(accountId, 'day', day)

    if (counts.pending === 0) {
      // Plus rien à confirmer : la notification a fait son travail. On ne la
      // supprime pas — elle reste comme trace de ce qui s'est passé.
      const existing = await db.notifications.get(id)
      if (existing && !existing.deleted_at && (existing.read_at ?? null) === null) {
        await setRead(id, true)
      }
      continue
    }

    const one = counts.pending === 1
    await ensureNotification(id, {
      kind: 'reminder',
      walletId,
      title: one
        ? 'Une journée prévue attend ta confirmation'
        : `${counts.pending} journées prévues attendent ta confirmation`,
      body: `${formatDay(day)} — ${formatAriary(counts.amount)} prévus. `
        + 'Dis ce qui a vraiment été dépensé pour que ton solde reste juste.',
      // Vers la JOURNÉE elle-même, et non vers l'onglet : déposer sur une
      // liste laisserait l'utilisateur chercher ce qu'on vient de lui
      // annoncer. `App` ouvre la feuille de confirmation de ce jour-là.
      route: dayTarget(day),
      // La date de la JOURNÉE, pas celle de l'ouverture de l'app : une
      // notification de mardi vue le jeudi reste une notification de mardi.
      occurredAt: `${day}T08:00:00.000Z`,
    })
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   2. Les enveloppes dépassées — gratuit par principe (vision.md)
   ───────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ `overspent` et non `remaining < 0` : une enveloppe CUMULATIVE qui dépasse
 * son objectif a trop épargné, ce qui est une bonne nouvelle. La confondre avec
 * un dépassement reviendrait à gronder quelqu'un d'avoir bien fait.
 *
 * Une notification par enveloppe et par CYCLE : le dépassement dure jusqu'au
 * bout du mois, et redire chaque jour la même chose ne fait que dresser
 * l'utilisateur à ignorer la cloche.
 */
async function overspentEnvelopes({ walletId, accountId, date }: Required<CatchUpInput>) {
  const summary = await loadSummary(walletId, date)
  const categories = await db.categories.where({ wallet_id: walletId }).toArray()
  const names = new Map(categories.map((c) => [c.id, c.name]))

  for (const envelope of summary.envelopes) {
    if (!envelope.overspent) continue

    const id = await seedId(accountId, 'overspent', envelope.category_id, summary.range.start)
    const name = names.get(envelope.category_id) ?? 'Une enveloppe'
    await ensureNotification(id, {
      kind: 'budget',
      walletId,
      title: `${name} est dépassée`,
      // Le dépassement en clair : « dépassée » sans le montant oblige à aller
      // le chercher, et une alerte qu'il faut instruire n'est pas une alerte.
      body: `${formatAriary(envelope.spent)} dépensés sur ${formatAriary(envelope.amount)} `
        + `prévus — ${formatAriary(-envelope.remaining)} de trop.`,
      route: '/budget',
    })
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   3. Les objectifs atteints
   ───────────────────────────────────────────────────────────────────────── */

/**
 * La seule notification agréable de la liste, et la seule qui récompense.
 *
 * `achieved_at` plutôt que le calcul en cours : il est justement RETENU pour
 * que la félicitation ne se rejoue pas, et un objectif dont on reprend l'argent
 * ne doit pas se refélicité au versement suivant.
 */
async function reachedGoals({ walletId, accountId, date }: Required<CatchUpInput>) {
  const goals = (await db.goals.where({ wallet_id: walletId }).toArray())
    .filter((g) => !g.deleted_at && (g.achieved_at ?? null) !== null)

  for (const goal of goals) {
    const id = await seedId(accountId, 'goal', goal.id)
    await ensureNotification(id, {
      kind: 'goal',
      walletId,
      title: `Objectif atteint : ${goal.name}`,
      body: `Tu as mis de côté ${formatAriary(goal.target_amount)}. `
        + 'Range-le pour récupérer cet argent, ou vise plus haut.',
      route: '/budget',
      occurredAt: goal.achieved_at ?? undefined,
    })
  }
  // `date` n'entre pas dans la graine : un objectif n'est atteint qu'une fois.
  void date
}

/* ─────────────────────────────────────────────────────────────────────────
   4. Trop longtemps sans synchronisation
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Le seuil de trois jours vient de `vision.md` (décision 2026-08-20).
 * L'avertissement existe déjà à l'écran, mais dort dans le Profil — or l'enjeu
 * est justement de ne pas découvrir le décalage trop tard.
 *
 * La graine porte la DERNIÈRE date de synchronisation : une seule notification
 * par épisode, et non une par jour d'absence. Elle reste en tête tant qu'elle
 * n'est pas lue ; en redire une chaque matin ne ferait qu'apprendre à l'ignorer.
 */
async function staleSync({ accountId }: { accountId: string }) {
  const days = await daysSinceLastSync()
  if (days === null || days < 3) return

  const last = (await db.meta.get('last_sync'))?.value as string | undefined
  const id = await seedId(accountId, 'stale-sync', last ?? 'jamais')
  await ensureNotification(id, {
    kind: 'system',
    title: `Aucune synchronisation depuis ${days} jours`,
    body: 'Tes données ne sont enregistrées que sur cet appareil. '
      + 'Synchronise dès que tu as du réseau.',
    route: '/profil',
  })
}

/* ─────────────────────────────────────────────────────────────────────────
   5. Les conflits à trancher
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Le plus important de tous, et le plus invisible.
 *
 * Un conflit non tranché laisse deux versions d'un même montant, et l'app en
 * affiche une : le solde est faux et rien ne le dit. Jusqu'ici il fallait
 * ouvrir Profil → Conflits pour le savoir, ce que personne ne fait sans raison.
 */
async function pendingConflicts({ accountId }: { accountId: string }) {
  const conflicts = await db.conflicts.toArray()

  for (const conflict of conflicts) {
    const id = await seedId(accountId, 'conflict', conflict.id)
    await ensureNotification(id, {
      kind: 'system',
      title: 'Une donnée a été modifiée à deux endroits',
      body: 'Deux appareils ont changé la même chose hors ligne. '
        + 'Choisis la bonne version : tant que ce n\'est pas fait, un montant '
        + 'peut être faux.',
      route: '/profil',
      occurredAt: conflict.detected_at,
    })
  }
}

/* ───────────────────────────────────────────────────────────────────────── */

/** L'identifiant calculé d'une notification. Le compte en fait TOUJOURS partie. */
function seedId(accountId: string, ...parts: string[]): Promise<string> {
  return derivedId([ SEED, accountId, ...parts ].join(':'))
}
